"use client";

import {
  useFormatter,
  useLocale,
  useTimeZone,
  useTranslations,
} from "next-intl";
import { useMemo } from "react";
import { type Locale, localeBcp47 } from "./locales";

/**
 * Locale-aware formatting for dates, numbers and currency.
 *
 * Replaces the ~525 places that hardcode `"de-DE"`. Two categories must NOT be
 * routed through here, and are documented as exemptions instead:
 *
 *   - **Machine formats.** `settings-usage-format.ts` uses `"en-CA"` to get
 *     `YYYY-MM-DD` and `settings-usage-fine-time.ts` uses `"en-GB"`. Those are
 *     data formats; translating them would corrupt the values.
 *   - **Sort keys.** `compare-german-labels.ts` and the persisted
 *     `properties.name_sort` column must stay German-collated — a list whose
 *     order changes with the interface language breaks cursor pagination.
 *
 * The time zone is pinned to Europe/Berlin in `src/i18n/request.ts`, so server
 * and client render identical strings regardless of where the server runs.
 */

/** BCP 47 tag for the active locale — for the rare direct `Intl` use. */
export function useLocaleTag(): string {
  const tLocales = useTranslations("common.locales");
  const locale = useLocale() as Locale;
  return localeBcp47(tLocales)[locale];
}

export interface DateFormatters {
  /** 24.12.2026 · 24/12/2026 */
  readonly date: (value: Date | number) => string;
  /** 24.12.2026, 14:30 · 24/12/2026, 14:30 */
  readonly dateTime: (value: Date | number) => string;
  /** 14:30 */
  readonly time: (value: Date | number) => string;
  /** 24. Dezember 2026 · 24 December 2026 */
  readonly long: (value: Date | number) => string;
  /** 24. Dez. 2026 · 24 Dec 2026 */
  readonly medium: (value: Date | number) => string;
  /** Sep. 2026 · Sept 2026 */
  readonly monthYear: (value: Date | number) => string;
  /** 07.03. · 07/03 */
  readonly dayMonth: (value: Date | number) => string;
  /** 1. April · 1 April — yearly key date without year (e.g. main due date). */
  readonly dayMonthLong: (value: Date | number) => string;
  /** vor 3 Tagen · 3 days ago */
  readonly relative: (value: Date | number, now?: Date | number) => string;
  /** Freitag, 2. Oktober 2026 · Friday, 2 October 2026 */
  readonly weekdayLong: (value: Date | number) => string;
  /** Oktober · October */
  readonly monthLong: (value: Date | number) => string;
  /** Okt. · Oct */
  readonly monthShort: (value: Date | number) => string;
  /** Oktober 2026 · October 2026 */
  readonly monthYearLong: (value: Date | number) => string;
  /** Fr · Fri (trailing dot dropped for column headers) */
  readonly weekdayShort: (value: Date | number) => string;
}

export function useFormatDate(): DateFormatters {
  const format = useFormatter();
  const localeTag = useLocaleTag();
  const timeZone = useTimeZone();

  return useMemo(() => {
    // Not `format.dateTime`: next-intl passes bare `en`, which Intl renders US-style.
    const formatter = (
      options: Intl.DateTimeFormatOptions,
    ): ((value: Date | number) => string) => {
      const intl = new Intl.DateTimeFormat(localeTag, { ...options, timeZone });
      return (value) => intl.format(value);
    };

    return {
      date: formatter({ day: "2-digit", month: "2-digit", year: "numeric" }),
      dateTime: formatter({
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      }),
      time: formatter({ hour: "2-digit", minute: "2-digit" }),
      long: formatter({ day: "numeric", month: "long", year: "numeric" }),
      medium: formatter({ day: "numeric", month: "short", year: "numeric" }),
      monthYear: formatter({ month: "short", year: "numeric" }),
      dayMonth: formatter({ day: "2-digit", month: "2-digit" }),
      dayMonthLong: formatter({ day: "numeric", month: "long" }),
      relative: (value, now) =>
        format.relativeTime(
          toDate(value),
          now === undefined ? undefined : toDate(now),
        ),
      weekdayLong: formatter({
        weekday: "long",
        day: "numeric",
        month: "long",
        year: "numeric",
      }),
      monthLong: formatter({ month: "long" }),
      monthShort: formatter({ month: "short" }),
      monthYearLong: formatter({ month: "long", year: "numeric" }),
      weekdayShort: ((): ((value: Date | number) => string) => {
        const base = formatter({ weekday: "short" });
        return (value) => base(value).replace(/\.$/, "");
      })(),
    };
  }, [format, localeTag, timeZone]);
}

export interface NumberFormatters {
  /** 1.234,5 · 1,234.5 */
  readonly decimal: (value: number, fractionDigits?: number) => string;
  /** 1.234,50 € · €1,234.50 */
  readonly currency: (value: number, currency?: string) => string;
  /** 42 % · 42% */
  readonly percent: (value: number) => string;
}

export function useFormatNumber(): NumberFormatters {
  const format = useFormatter();

  return useMemo(
    () => ({
      decimal: (value, fractionDigits) =>
        format.number(value, {
          minimumFractionDigits: fractionDigits,
          maximumFractionDigits: fractionDigits,
        }),
      // Euro stays the default: the product is German property management, so
      // the currency follows the money, not the reader's language.
      currency: (value, currency = "EUR") =>
        format.number(value, { style: "currency", currency }),
      percent: (value) => format.number(value, { style: "percent" }),
    }),
    [format],
  );
}

function toDate(value: Date | number): Date {
  return value instanceof Date ? value : new Date(value);
}

export interface DurationFormatters {
  /** 7:30 h (minutes in) */
  readonly hours: (minutes: number) => string;
  /** +1:15 h · −0:30 h · 0:00 h */
  readonly signedHours: (minutes: number) => string;
  /** 7:30 (no unit, for dense grids) */
  readonly clock: (minutes: number) => string;
  /** 1,5 · 1.5 (days, half-day precision) */
  readonly days: (days: number) => string;
}

/** Durations stay H:MM in every locale; only the day count is locale-formatted. */
export function useFormatDuration(): DurationFormatters {
  const format = useFormatter();
  return useMemo(() => {
    const clock = (minutes: number): string => {
      const abs = Math.abs(Math.round(minutes));
      const h = Math.floor(abs / 60);
      const m = abs % 60;
      return `${h}:${m.toString().padStart(2, "0")}`;
    };
    return {
      hours: (minutes) => `${minutes < 0 ? "−" : ""}${clock(minutes)} h`,
      signedHours: (minutes) => {
        const rounded = Math.round(minutes);
        const sign = rounded > 0 ? "+" : rounded < 0 ? "−" : "";
        return `${sign}${clock(rounded)} h`;
      },
      clock: (minutes) => `${minutes < 0 ? "−" : ""}${clock(minutes)}`,
      days: (days) => format.number(days, { maximumFractionDigits: 1 }),
    };
  }, [format]);
}
